import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Upload } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

const SUGESTOES = ["Conta de Luz", "TV", "Água", "Aluguel", "Internet", "Produtos", "Outros"];

function parseMoney(v) {
  return parseFloat(String(v).replace(/\./g, "").replace(",", ".")) || 0;
}

export default function GoalForm({ meta, submitLabel = "SALVAR", onSaved }) {
  const isEdit = !!meta;
  const [form, setForm] = useState({
    referente: meta?.referente || "",
    beneficiario: meta?.beneficiario || "",
    cnpj: meta?.cnpj || "",
    descricao: meta?.descricao || "",
    valor_total: meta ? String(meta.valor_total).replace(".", ",") : "",
    valor_arrecadado: meta ? String(meta.valor_arrecadado).replace(".", ",") : "0",
    vencimento: meta?.vencimento || "",
    chave_pix: meta?.chave_pix || "",
    ordem: meta?.ordem ?? 0,
    ativo: meta?.ativo ?? false,
  });
  const [qrFile, setQrFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      referente: form.referente,
      beneficiario: form.beneficiario,
      cnpj: form.cnpj,
      descricao: form.descricao,
      valor_total: parseMoney(form.valor_total),
      valor_arrecadado: parseMoney(form.valor_arrecadado),
      vencimento: form.vencimento,
      chave_pix: form.chave_pix,
      ordem: parseInt(form.ordem) || 0,
      ativo: form.ativo,
    };
    if (!payload.referente) return toast.error("Informe o campo Referente.");
    if (payload.valor_total <= 0) return toast.error("Informe um valor total válido.");
    if (!payload.vencimento) return toast.error("Informe a data de vencimento.");
    if (payload.valor_arrecadado > payload.valor_total) return toast.error("O valor arrecadado não pode ser maior que o valor total.");
    setSaving(true);
    try {
      let metaId;
      if (isEdit) {
        await api.put(`/admin/metas/${meta.id}`, payload);
        metaId = meta.id;
      } else {
        const res = await api.post("/admin/metas", payload);
        metaId = res.data.id;
      }
      if (qrFile) {
        const fd = new FormData();
        fd.append("file", qrFile);
        await api.post(`/admin/metas/${metaId}/qrcode`, fd);
      }
      toast.success(isEdit ? "Meta atualizada!" : "Boleto / Meta gerado! Use Visualizar, Editar ou Publicar na aba Metas.");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 space-y-4">
      <div>
        <Label className="text-base font-semibold text-slate-700">Referente *</Label>
        <Input
          data-testid="goal-form-referente"
          list="referente-sugestoes"
          value={form.referente}
          onChange={set("referente")}
          placeholder="Ex.: Conta de Luz"
          className="mt-1.5 h-12 text-base rounded-xl"
        />
        <datalist id="referente-sugestoes">
          {SUGESTOES.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-base font-semibold text-slate-700">Beneficiário</Label>
          <Input data-testid="goal-form-beneficiario" value={form.beneficiario} onChange={set("beneficiario")} placeholder="Ex.: Enel" className="mt-1.5 h-12 text-base rounded-xl" />
        </div>
        <div>
          <Label className="text-base font-semibold text-slate-700">CNPJ</Label>
          <Input data-testid="goal-form-cnpj" value={form.cnpj} onChange={set("cnpj")} placeholder="00.000.000/0000-00" className="mt-1.5 h-12 text-base rounded-xl" />
        </div>
      </div>
      <div>
        <Label className="text-base font-semibold text-slate-700">Descrição</Label>
        <Textarea data-testid="goal-form-descricao" value={form.descricao} onChange={set("descricao")} placeholder="Detalhes da despesa..." className="mt-1.5 text-base rounded-xl min-h-[70px]" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-base font-semibold text-slate-700">Valor total (R$) *</Label>
          <Input data-testid="goal-form-valor-total" value={form.valor_total} onChange={set("valor_total")} placeholder="Ex.: 5000,00" inputMode="decimal" className="mt-1.5 h-12 text-base rounded-xl" />
        </div>
        <div>
          <Label className="text-base font-semibold text-slate-700">Já arrecadado (R$)</Label>
          <Input data-testid="goal-form-valor-arrecadado" value={form.valor_arrecadado} onChange={set("valor_arrecadado")} placeholder="0,00" inputMode="decimal" className="mt-1.5 h-12 text-base rounded-xl" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-base font-semibold text-slate-700">Data de vencimento *</Label>
          <Input data-testid="goal-form-vencimento" type="date" value={form.vencimento} onChange={set("vencimento")} className="mt-1.5 h-12 text-base rounded-xl" />
        </div>
        <div>
          <Label className="text-base font-semibold text-slate-700">Ordem dos cartões</Label>
          <Input data-testid="goal-form-ordem" type="number" value={form.ordem} onChange={set("ordem")} className="mt-1.5 h-12 text-base rounded-xl" />
        </div>
      </div>
      <div>
        <Label className="text-base font-semibold text-slate-700">Chave Pix</Label>
        <Input data-testid="goal-form-chave-pix" value={form.chave_pix} onChange={set("chave_pix")} placeholder="E-mail, CNPJ, telefone ou chave aleatória" className="mt-1.5 h-12 text-base rounded-xl" />
        <p className="mt-1 text-xs text-slate-400">Deixe em branco para usar a chave Pix padrão das Configurações. O QR Code é gerado automaticamente a partir da chave.</p>
      </div>
      <div>
        <Label className="text-base font-semibold text-slate-700">QR Code Pix (imagem, opcional)</Label>
        <label htmlFor="qr-upload" className="mt-1.5 flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 text-slate-600 cursor-pointer transition-colors text-base">
          <Upload className="w-5 h-5" />
          {qrFile ? qrFile.name : isEdit && meta?.tem_qr_code ? "QR Code atual enviado — tocar para substituir" : "Toque para enviar a imagem do QR Code"}
        </label>
        <input id="qr-upload" data-testid="goal-form-qrcode-upload" type="file" accept="image/*" className="hidden" onChange={(e) => setQrFile(e.target.files?.[0] || null)} />
      </div>
      <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
        <Label className="text-base font-semibold text-slate-700">Publicar no painel (visível para todos)</Label>
        <Switch data-testid="goal-form-ativo" checked={form.ativo} onCheckedChange={(v) => setForm((f) => ({ ...f, ativo: v }))} />
      </div>
      <button
        type="submit"
        data-testid="goal-form-submit"
        disabled={saving}
        className="w-full py-4 rounded-xl bg-blue-800 hover:bg-blue-700 disabled:opacity-60 text-white font-bold text-lg shadow-md transition-colors flex items-center justify-center gap-2"
      >
        {saving && <Loader2 className="w-5 h-5 animate-spin" />}
        {saving ? "Gerando..." : submitLabel}
      </button>
    </form>
  );
}
